import {PIROEntityTypes} from '@/enums/PIROEntityTypes'
import {formActions} from '@/enums/formActions'

const productInitState = () => ({
  name: '',
  subItems: [],
  variants: [],
  productImageURLs: [],
})

const initState = () => ({
  showFilter: false,
  products: [],
  attributes: [],
  product: productInitState(),
  category: {},
  priceCancelToken: null,
  productReplacedWithChildItemType: false,
  itemCount: 0,
})

export const state = initState

export const getters = {
  getMetalSubItemID(state) {
    if (
      state.product.subItems.some(
        (si) => si.attachedEntityTypeId === PIROEntityTypes.MasterAlloys
      )
    ) {
      return state.product.subItems.find(
        (si) => si.attachedEntityTypeId === PIROEntityTypes.MasterAlloys
      ).itemTypeID
    }
    return 0
  },
  getSubItems(state) {
    return state.product.subItems
  },
  getVariants(state) {
    return state.product.variants
  },
  productHasMetalSubitem(state) {
    return state.product.subItems.some(
      (s) => s.attachedEntityTypeId === PIROEntityTypes.MasterAlloys
    )
  },
}

export const mutations = {
  setProducts(state, {products}) {
    state.products = products
  },
  addProducts(state, {products}) {
    state.products.push(...products)
  },
  addAttributes(state, {attributes}) {
    state.attributes.push(...attributes)
  },
  setProduct(state, {product}) {
    state.product = product
  },
  setProductPrice(state, price) {
    state.product.price = price
  },
  setPriceCancelToken(state, priceCancelToken) {
    state.priceCancelToken = priceCancelToken
  },
  reset(state) {
    Object.assign(state, initState())
  },
  setProductImages(state, {images}) {
    state.product.productImageURLs = []
    state.product.productImageURLs = [...images]
  },
  setCategory(state, {category}) {
    state.category = category
  },
  initCategory(state) {
    state.category = {
      name: '',
    }
  },
  setProductByChildItemType(state, childItemType) {
    state.productReplacedWithChildItemType = false;

    if (childItemType && childItemType.id > 0) {
      state.product.id = childItemType.id;
      state.product.code = childItemType.code;
      state.product.name = childItemType.name;
      state.productReplacedWithChildItemType = true;
    }
  },
  emptyProducts(state) {
    state.products = []
  },
  setItemCount(state, itemCount) {
    state.itemCount = itemCount
  },
  resetProduct(state){
    state.product = productInitState()
  }
}

export const actions = {
  async getProducts({commit}, {id, filters}) {
    commit('setProducts', {products: []})
    const result = await this.$axios.$get(
      `/productCategories/products?categoryId=${id}`
    )
    const products = result.Items;
    const itemCount = result.pagingData.itemCount
    commit('setProducts', {products})
    commit('setItemCount', itemCount)
  },
  async getCategoryById({commit}, id) {
    commit('initCategory')
    const category = await this.$axios.$get(`/productCategories/${id}`)
    commit('setCategory', {category})
  },
  getFilteredProducts({commit}, {id, itemGroupID, filters, pagingInfo, forTable = false}) {
    return new Promise((resolve, reject) => {
      this.$catch(
        async () => {
          let url = `/productCategories/products?categoryId=${id}`
          const attributeFilters = []
          filters.map((f) => {
            if (f.value) {
              if (f.property.indexOf('ATTR_') == 0) {
                f.value = f.value === true ? 1 : f.value === false ? 0 : f.value;

                attributeFilters.push({
                  value: f.value,
                  attributeCode: f.property.replace('ATTR_', ''),
                })
              } else {
                url = url + `&${f.property}=${f.value}`
              }
            }
            if(f.valuefrom){
              url = url + `&${f.property}from=${f.valuefrom}`;
            }
            if(f.valueto){
              url = url + `&${f.property}to=${f.valueto}`;
            }
          })

          url = url + `&attributeFilterStr=${JSON.stringify(attributeFilters)}`
          url = url + `&itemGroupID=${itemGroupID}`
          url = url + `&pageNo=${pagingInfo.pageNo}&pageSize=${pagingInfo.pageSize}`
          const result = await this.$axios.$get(url)
          const products = result.items;
          const itemCount = result.pagingData.itemCount
          commit('setItemCount', itemCount)
          if(!forTable) {
            if (pagingInfo.pageNo === 0) {
              commit('setProducts', {products})
            } else {
              commit('addProducts', {products})
            }
          }
          else if (forTable) {
            commit('setProducts', {products})
          }
          resolve(products)
        },
        formActions.Load,
        'products'
      )
    })
  },
  getFilterAttributes({commit}, {id}) {
    return new Promise((resolve, reject) => {
      this.$catch(
        async () => {
          const url = `/productCategories/filterAttributes?itemGroupID=${id}`

          const attributes = await this.$axios.$get(url)

          commit('addAttributes', {attributes})

          resolve(attributes)
        },
        formActions.Load,
        'attributes'
      )
    })
  },
  getFilterAttributesByCategory({commit}, {id}){
    return new Promise((resolve, reject) => {
      this.$catch(
        async () => {
          const url = `/productCategories/filterAttributesByCategory${id ? `?categoryID=${id}` : ''}`

          const attributes = await this.$axios.$get(url)

          commit('addAttributes', {attributes})

          resolve(attributes)
        },
        formActions.Load,
        'attributes'
      )
    })
  },
  getCategoryByCode({ commit }, { code }) {
    return new Promise((resolve, reject) => {
      this.$catch(
        async () => {
          const url = `/productCategories/categoryCode/${code}`

          const category = await this.$axios.$get(url)

          commit('setCategory', { category })

          resolve(category)
        },
        formActions.Load,
        'category'
      )
    })
  },
  async getProductById({commit}, productId) {
    commit('setProduct', {product: productInitState()})
    const product = await this.$axios.$get(
      `/itemtypes/${productId}?includesubitems=true&includevariants=true`
    )
    commit('setProduct', {product})
  },
  async updateProductPrice({commit, state}, {priceCalculationData}) {
    if (state.priceCancelToken) {
      state.priceCancelToken.cancel()
    }
    const priceCancelToken = this.$axios.CancelToken.source()
    commit('setPriceCancelToken', priceCancelToken)
    const price = await this.$axios.$post(
      `/products/cp/calculatePrice`,
      priceCalculationData,
      {
        cancelToken: priceCancelToken.token,
      }
    )
    commit('setProductPrice', price)
  },
  async updateProductImages({commit}, {productCode, metalId}) {
    const images = await this.$axios.$get(
      `/products/updateProductImages?productCode=${productCode}&metalId=${metalId}`
    )
    commit('setProductImages', {images})
  },
  async getItemTypeByMasterItemTypeAttributes({commit}, {productId, attributeValues}) {
    const response = await this.$axios.$post(
      '/products/getChildItemByMasterItemTypeAttributes',
      {
        itemTypeID: productId,
        attributes: attributeValues
      });
    commit('setProductByChildItemType', response.value)
  },
  async fetchRetailProducts({commit}, {filters}) {
    let url = `/ProductCategories/RetailProducts`

    if (filters) {
      let prefix = '?'
      if(filters.pageNo){
        url = url + prefix + `PageNo=${filters.pageNo}`
        prefix = '&'
      }
      if(filters.pageSize){
        url = url + prefix + `PageSize=${filters.pageSize}`
        prefix = '&'
      }
      if(filters.featuredOnPortal){
        url = url + prefix + `FeaturedProductOnPortal=${filters.featuredOnPortal}`
        prefix = '&'
      }
    }

    return await this.$axios.$get(url)
  },
  resetProducts({commit}) {
    commit('emptyProducts')
  },
  resetProduct({commit}) {
    commit('resetProduct')
  }

}
